import * as React from "react"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "../../../lib/utils"

const headingBaseStyles = "font-sans tracking-tight text-foreground"

export interface HeadingBaseProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Render as a different element using Radix Slot
   */
  asChild?: boolean
}

// ============================================
// Heading1
// ============================================

export type Heading1Props = HeadingBaseProps

/**
 * Heading1 component for page titles and hero sections.
 */
function Heading1({ className, asChild = false, children, ...props }: Heading1Props) {
  const Comp = asChild ? Slot : "h1"

  return (
    <Comp
      data-slot="heading1"
      className={cn(headingBaseStyles, "text-5xl font-bold leading-tight", className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Heading1.displayName = "Heading1"

// ============================================
// Heading2
// ============================================

export type Heading2Props = HeadingBaseProps

/**
 * Heading2 component for section headers.
 */
function Heading2({ className, asChild = false, children, ...props }: Heading2Props) {
  const Comp = asChild ? Slot : "h2"

  return (
    <Comp
      data-slot="heading2"
      className={cn(headingBaseStyles, "text-4xl font-semibold leading-snug", className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Heading2.displayName = "Heading2"

// ============================================
// Heading3
// ============================================

export type Heading3Props = HeadingBaseProps

/**
 * Heading3 component for subsection headers.
 */
function Heading3({ className, asChild = false, children, ...props }: Heading3Props) {
  const Comp = asChild ? Slot : "h3"

  return (
    <Comp
      data-slot="heading3"
      className={cn(headingBaseStyles, "text-3xl font-semibold leading-snug", className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Heading3.displayName = "Heading3"

// ============================================
// Heading4
// ============================================

export type Heading4Props = HeadingBaseProps

/**
 * Heading4 component for card titles and modal headers.
 */
function Heading4({ className, asChild = false, children, ...props }: Heading4Props) {
  const Comp = asChild ? Slot : "h4"

  return (
    <Comp
      data-slot="heading4"
      className={cn(headingBaseStyles, "text-2xl font-medium leading-normal", className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Heading4.displayName = "Heading4"

// ============================================
// Heading5
// ============================================

export type Heading5Props = HeadingBaseProps

/**
 * Heading5 component for small section titles.
 */
function Heading5({ className, asChild = false, children, ...props }: Heading5Props) {
  const Comp = asChild ? Slot : "h5"

  return (
    <Comp
      data-slot="heading5"
      className={cn(headingBaseStyles, "text-xl font-medium leading-normal", className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Heading5.displayName = "Heading5"

// ============================================
// Heading6
// ============================================

export type Heading6Props = HeadingBaseProps

/**
 * Heading6 component for inline headings and labels.
 */
function Heading6({ className, asChild = false, children, ...props }: Heading6Props) {
  const Comp = asChild ? Slot : "h6"

  return (
    <Comp
      data-slot="heading6"
      className={cn(headingBaseStyles, "text-lg font-medium leading-normal", className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Heading6.displayName = "Heading6"

export { Heading1, Heading2, Heading3, Heading4, Heading5, Heading6 }
